import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, InputAdornment, IconButton, Chip, Typography, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';

/**
 * Suchfeld für den In-Place-Filter des Boards.
 *
 * Hält nur den Eingabezustand und entprellt ihn; gesucht wird im Board selbst.
 * `Esc` leert das Feld (und damit den Filter), `/` fokussiert es von überall
 * auf der Seite – solange man nicht ohnehin gerade in einem Eingabefeld tippt.
 */
const DEBOUNCE_MS = 300;

function BoardSearchBar({ value, onChange, allProjects, onAllProjectsChange, loading, summary }) {
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  // Zurücksetzen von außen (z. B. Projektwechsel) muss im Feld ankommen.
  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    if (draft === value) return undefined;
    const timer = setTimeout(() => onChange(draft), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, value, onChange]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const clear = () => { setDraft(''); onChange(''); };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <TextField
        size="small"
        inputRef={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); clear(); } }}
        placeholder="Tickets durchsuchen – Titel, Text, Kommentare, #Nummer"
        sx={{ minWidth: 340, flex: '1 1 340px', maxWidth: 520 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
            </InputAdornment>
          ),
          endAdornment: draft ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={clear} aria-label="Suche zurücksetzen">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {draft && (
        <Chip
          size="small"
          label="Alle Projekte"
          color={allProjects ? 'primary' : 'default'}
          variant={allProjects ? 'filled' : 'outlined'}
          onClick={() => onAllProjectsChange(!allProjects)}
        />
      )}

      {summary && (
        <Typography variant="caption" color="text.secondary">{summary}</Typography>
      )}
    </Box>
  );
}

export default BoardSearchBar;
